import { useCallback, useMemo, useState } from 'react'

import { Skeleton } from 'antd'
import { Flex, DateRangePicker } from 'govwell-ui'
import {
  DateRangeType,
  DateRangePickerValue,
} from 'govwell-ui/components/DateRangePicker/DateRangePicker'
import { getDateRangePickerValue } from 'govwell-ui/components/DateRangePicker/util'
import {
  DateRange,
  OrderMode,
  ViewType,
  WidgetAggregationFn,
  WidgetDataGroupSortBy,
} from 'types/graphql'

import AIPageContainer from 'src/components/AIAssistant/AIPageContainer'
import BarChart from 'src/components/Visualizations/BarChart/BarChart'
import CirclePackingChart from 'src/components/Visualizations/CirclePackingChart/CirclePackingChart'
import Tile, { TileType } from 'src/components/Visualizations/Tile'
import {
  extractViewDataElementNumber,
  getBucketStartLabel,
} from 'src/components/Visualizations/util'
import { useAIROIQuery } from 'src/fetch/aiChat'
import { useGetMostUsedAIChatTopicsQuery } from 'src/fetch/aiChatTopics'
import { useGetDataForWidgetQuery } from 'src/fetch/widgets'
import { usePersistentIds } from 'src/hooks/use-persistent-ids'
import { useMyOrg } from 'src/layouts/AppStateContextLayout/utils'
import { filterNil } from 'src/utils'
import { DateFormats } from 'src/utils/date'
import { FeatureGate, useFeatureFlagging } from 'src/utils/featureFlagging/featureFlagging'
import { monitoring, MonitoringContextKey, MonitoringEvent } from 'src/utils/monitoring'
import { formatNumber, NumberFormat } from 'src/utils/number'

import TopicFrequencyTable from './TopicFrequencyTable'

const dateKey = 'Date'
const substantiveAnswerYesKey = 'Answered automatically'
const substantiveAnswerNoKey = 'Not answered automatically'
const keys = [substantiveAnswerNoKey, substantiveAnswerYesKey] // bottom-to-top

const CommunityAssistantInsightsPage = () => {
  const { sessionId } = usePersistentIds()
  const org = useMyOrg()

  const [dateRange, setDateRange] = useState<DateRangePickerValue>(
    getDateRangePickerValue({ type: DateRangeType.MonthToDate, timeZone: org?.timeZone })
  )

  const selectedDateRange: DateRange = useMemo(() => {
    return {
      start: dateRange.startDate?.toISOString() ?? '',
      end: dateRange.endDate?.toISOString() ?? '',
    }
  }, [dateRange])

  const { aiChatROIData, isLoading } = useAIROIQuery({
    startDate: selectedDateRange.start,
    endDate: selectedDateRange.end,
  })

  const responseWasSubstantiveQueryInput = useMemo(() => {
    const functions: WidgetAggregationFn[] = ['Count']
    const groupSortBy: WidgetDataGroupSortBy = 'Key'
    const groupSortMode: OrderMode = 'Asc'
    const viewType: ViewType = 'CommunityAssistantAIChats'
    return {
      functionInputColumnTemplate: null,
      functions,
      dateRange: selectedDateRange,
      dateRangeColumnTemplate: { columnTemplateGeneratedId: 'AssistantAIQuestionCreatedAt' },
      filters: [],
      groupByColumnTemplates: [
        { columnTemplateGeneratedId: 'CommunityAssistantAIResponseWasSubstantive' },
      ],
      groupSortBy,
      groupSortMode,
      viewType,
      isTimeSeries: true,
    }
  }, [selectedDateRange])

  const {
    buckets: timeDataBuckets,
    bucketSize: timeBucketSize,
    isLoading: isTimeSeriesLoading,
  } = useGetDataForWidgetQuery(responseWasSubstantiveQueryInput)

  // Fetch most used topics
  const {
    results: topicsResults,
    isLoading: isTopicsLoading,
    error: topicsError,
    percentCovered,
  } = useGetMostUsedAIChatTopicsQuery({
    organizationId: org?.id ?? 0,
    startDate: selectedDateRange.start,
    endDate: selectedDateRange.end,
  })

  const timeSeriesData = useMemo(() => {
    if (!timeDataBuckets || !timeBucketSize) {
      return []
    }

    return filterNil(
      timeDataBuckets.map((bucket) => {
        if (bucket.groups?.length === 0) {
          return null
        }

        const row: Record<string, number | string> = {
          [dateKey]: getBucketStartLabel(timeBucketSize, bucket.start, org?.timeZone),
        }

        bucket.groups?.forEach((group) => {
          const value = extractViewDataElementNumber(group.values?.[0]?.value) ?? 0
          const key = group.groupBys?.[0]?.key
          if (key?.__typename === 'ViewDataElementBoolean') {
            const name = key?.bool ? substantiveAnswerYesKey : substantiveAnswerNoKey
            row[name] = value
          }
        })

        return row
      })
    )
  }, [timeDataBuckets, timeBucketSize, org?.timeZone])

  const { numberOfAIChats, percentOfAIChatsWithSubstantiveAnswer, percentOfAIChatsWithThumbsUp } =
    useMemo(() => {
      return {
        numberOfAIChats: aiChatROIData?.numberOfAIChats ?? 0,
        percentOfAIChatsWithSubstantiveAnswer:
          aiChatROIData?.percentOfAIChatsWithSubstantiveAnswer ?? 0,
        percentOfAIChatsWithThumbsUp: aiChatROIData?.percentOfAIChatsWithThumbsUp ?? 0,
      }
    }, [aiChatROIData])

  // Build circle packing nodes from topics results
  const circlePackingNodes = useMemo(
    () =>
      topicsResults.map(({ aiChatTopic, count }) => ({
        id: aiChatTopic.id.toString(),
        label: aiChatTopic.topicName,
        value: count,
      })),
    [topicsResults]
  )

  // Handle topic click for monitoring
  const handleTopicClick = useCallback(
    (topicId: string) => {
      if (!sessionId) {
        return
      }
      monitoring.captureEvent(MonitoringEvent.AiHotTopicClicked, {
        [MonitoringContextKey.AiChatSessionId]: sessionId,
        [MonitoringContextKey.AiTopicId]: topicId,
      })
    },
    [sessionId]
  )

  // Handle topic hover (no-op for now, can be used for future styling)
  const handleTopicHover = useCallback((_topicId: string | null) => {
    // No-op for now, can be used for future styling/highlighting
  }, [])

  const { checkFeatureFlag } = useFeatureFlagging()
  const isAIChatTopicsEnabled = checkFeatureFlag(FeatureGate.AiChatTopics)

  return (
    <AIPageContainer
      title="Community Assistant Insights"
      subtitle="Performance, usage metrics, and conversation insights"
    >
      <Flex direction="column" gap={4}>
        <DateRangePicker
          label="Date Range"
          dateFormat={DateFormats.MonthNameDateNoTime}
          value={dateRange}
          onChange={setDateRange}
          timeZone={org?.timeZone}
          width="100%"
        />
        {isLoading && <Skeleton active />}
        {!isLoading && (
          <Flex gap={2}>
            <Tile
              type={TileType.Number}
              value={numberOfAIChats}
              label="Questions received"
              tooltip="The number of questions received by the Community Assistant."
            />
            {!!percentOfAIChatsWithSubstantiveAnswer && (
              <Tile
                type={TileType.Percent}
                value={percentOfAIChatsWithSubstantiveAnswer}
                label="Questions answered automatically"
                tooltip="The percent of questions that the Community Assistant answered automatically."
              />
            )}
            {!!percentOfAIChatsWithThumbsUp && (
              <Tile
                type={TileType.Percent}
                value={percentOfAIChatsWithThumbsUp}
                label="Answers given 'thumbs up' by the public"
                tooltip="The percent of the Community Assistant's answers that received a thumbs-up reaction."
              />
            )}
          </Flex>
        )}
        <Flex gap={2}>
          <BarChart
            data={timeSeriesData}
            indexBy={dateKey}
            keys={keys}
            bottomAxisLabel="Date"
            leftAxisLabel="Questions"
            label="Questions Asked by Date"
            labelTooltip="Total questions, for each date bucket"
            isLoading={isTimeSeriesLoading}
            isTimeSeries
          />
        </Flex>
        {isAIChatTopicsEnabled && (
          <>
            <CirclePackingChart
              nodes={circlePackingNodes}
              isLoading={isTopicsLoading}
              error={topicsError ?? null}
              emptyMessage="No topics recorded for this range."
              subLabel={
                percentCovered
                  ? `${formatNumber(percentCovered, { format: NumberFormat.Percentage })} of conversations within this time range are covered by the most used topics.`
                  : undefined
              }
              label="Top Topics in Date Range"
              labelTooltip="The most frequently discussed topics by Community Assistant users for the selected date range"
              onClick={handleTopicClick}
              onHover={handleTopicHover}
            />
            <TopicFrequencyTable topics={circlePackingNodes} isLoading={isTopicsLoading} />
          </>
        )}
      </Flex>
    </AIPageContainer>
  )
}

export default React.memo(CommunityAssistantInsightsPage)
